import React, { useMemo, useState, useEffect } from "react"
import styled from "styled-components"
import { IconButton, Tooltip } from "@mui/material"
import { MdClose, MdInfo } from "react-icons/md"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import {
    MobergButton,
    MobergButtonShape,
    MobergButtonVariant,
    MobergTheme,
} from "../../../../Moberg"
import { humanReadableSize } from "../../../../Computation/utilFunctions"
import { FileSelectDropdown } from "../../../../Components/FileSelectDropdown/FileSelectDropdown"


const Container = styled.div`
    background: var(--color-surface-primary);
    color: var(--color-text-primary);
    padding: 24px 28px;
    border-radius: 8px;
    width: 520px;
    max-width: 92vw;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
`

const HeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
`

const Header = styled.h2`
    margin: 0;
    font-size: 20px;
    color: var(--color-text-primary);
`

const Subheader = styled.div`
    color: var(--text-secondary-color);
    font-size: 14px;
    margin-bottom: 16px;
`

const SectionLabel = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-family: 'Source Sans Pro';
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 8px;
`

const LabelCount = styled.span`
    font-family: 'Source Sans Pro';
    font-size: 13px;
    font-weight: 400;
    color: var(--text-secondary-color);
`

const FormatRow = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 18px;
`

/* Each format is a selectable card with a one-line description, so the user can
   compare HDF5 / EDF / CSV at a glance instead of guessing from a bare label. */
const FormatCard = styled.button`
    flex: 1;
    min-width: 0;
    /* Stack content from the top so all card titles align on the same line,
       even when one description wraps to more lines than another. */
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
    background: transparent;
    border: 1.5px solid
        ${({ $active }) =>
            $active ? "var(--color-accent-interactive)" : "var(--color-border-default)"};
    border-radius: 8px;
    padding: 10px 12px;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    transition: background 0.15s ease, border-color 0.15s ease;
    &:hover {
        border-color: ${({ $disabled }) =>
            $disabled ? undefined : "var(--color-accent-interactive)"};
    }
`

/* Reserve space for the per-format info box so selecting a format never resizes
   the modal. Sized to the tallest case (CSV, which adds the Include-EEG row). */
const InfoSlot = styled.div`
    min-height: 132px;
`

const FormatCardTitle = styled.div`
    font-family: 'Source Sans Pro';
    font-size: 14px;
    font-weight: 700;
    color: ${({ $active }) =>
        $active ? "var(--color-accent-interactive)" : "var(--color-text-primary)"};
`

const FormatCardDesc = styled.div`
    font-family: 'Source Sans Pro';
    font-size: 12px;
    color: var(--text-secondary-color);
    margin-top: 3px;
    line-height: 1.3;
`

// Matches the design-system InfoCard pattern used elsewhere (see e.g.
// ChangePassword.js): light-blue tint, blue 1px outline, MdInfo icon on the
// left. Same visual language across the app for informational callouts.
const InfoCard = styled.div`
    display: flex;
    align-items: flex-start;
    background: var(--color-hover-neutral);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 12px;
    margin-bottom: 4px;
`

const InfoIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 10px;
    margin-top: 1px;
`

const InfoText = styled.p`
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 13px;
    color: var(--color-text-primary);
    line-height: 150%;
    margin: 0;
`

const ButtonRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
`


const EXPORT_FORMATS = [
    { id: "HDF5", label: "HDF5", available: true, desc: "Full fidelity, all signals" },
    { id: "EDF", label: "EDF", available: true, desc: "EEG channels only, standard format" },
    { id: "CSV", label: "CSV", available: true, desc: "One CSV per signal, zipped" },
]

export default function ExportConfirmModal({ patient, onSuccess }) {
    const { close } = useModalProvider()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()

    const eligibleFiles = useMemo(() => {
        const dataObjects = patient?.data_objects || []
        let files
        if (dataObjects.length > 0) {
            // Non-clinical shape: filter to CNS files that exist on disk and have bytes.
            files = dataObjects
                .filter(
                    f => f.obj_type === "CNS"
                        && f.file_exists
                        && Number(f.filesize || 0) > 0
                )
                .map(f => ({
                    id: f.obj_id ?? f.id,
                    name: f.obj_name || `data_object #${f.obj_id ?? f.id}`,
                    size: Number(f.filesize || 0),
                    start: f.start_time || f._start_time || null,
                    end: f.end_time || f._end_time || null,
                }))
        } else {
            // Clinical fallback: row only has parallel arrays. Surface every file id that
            // exists on disk; POST /v1/exports validates obj_type/path, so non-CNS picks
            // return 400 cleanly.
            const ids = Array.isArray(patient?.data_file_ids) ? patient.data_file_ids : []
            const names = Array.isArray(patient?.data_file_name) ? patient.data_file_name : []
            const exists = Array.isArray(patient?.data_file_exists) ? patient.data_file_exists : []
            // Per-file arrays, parallel to data_file_ids. Fall back gracefully if the
            // backend doesn't supply them (older response shape).
            const sizes = Array.isArray(patient?.data_file_sizes) ? patient.data_file_sizes : []
            const starts = Array.isArray(patient?.data_file_start_times) ? patient.data_file_start_times : []
            const ends = Array.isArray(patient?.data_file_end_times) ? patient.data_file_end_times : []
            files = ids
                .map((id, i) => ({ id, name: names[i] || `data_object #${id}`, exists: exists[i] !== false, idx: i }))
                .filter(f => f.exists)
                .map(f => ({
                    id: f.id,
                    name: f.name,
                    // f.idx is the original index, so the per-file arrays line up after filtering.
                    size: Number(sizes[f.idx] || 0),
                    start: starts[f.idx] || null,
                    end: ends[f.idx] || null,
                }))
        }
        // Newest recording first; files with no start time sort to the bottom.
        return files.slice().sort((a, b) => {
            const ta = a.start ? new Date(a.start).getTime() : -Infinity
            const tb = b.start ? new Date(b.start).getTime() : -Infinity
            return tb - ta
        })
    }, [patient])

    // Pre-select the first file (and keep a valid selection if the list changes).
    const [selectedId, setSelectedId] = useState(eligibleFiles[0]?.id ?? null)
    const [selectedFormat, setSelectedFormat] = useState("HDF5")
    // Default unchecked — fast CSV. User opts in to include EEG, which is the
    // single biggest contributor to convert time and zip size for most archives.
    // Only sent on the wire when selectedFormat === 'CSV'.
    const [includeEEG, setIncludeEEG] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    // Available export storage, fetched once on open. null while loading or if
    // the stats call fails — in which case we don't block (fail open; the
    // worker still fails gracefully on a genuinely full disk).
    const [diskFreeBytes, setDiskFreeBytes] = useState(null)

    useEffect(() => {
        let cancelled = false
        endpointProvider.get(LINKS.DATA.EXPORTS.STATS, {})
            .then(stats => {
                if (cancelled) return
                const free = stats?.disk_free_bytes
                setDiskFreeBytes(typeof free === "number" ? free : null)
            })
            .catch(() => { /* fail open — leave null, don't block the user */ })
        return () => { cancelled = true }
    }, [endpointProvider, LINKS])

    const selectedFile = useMemo(
        () => eligibleFiles.find(f => f.id === selectedId) || null,
        [eligibleFiles, selectedId]
    )

    // Keep a valid selection: default to the first file once the list loads, and
    // recover if the current selection drops out of the list. eligibleFiles is
    // sorted newest-first, so [0] is the most recent recording.
    useEffect(() => {
        if (eligibleFiles.length > 0 && !eligibleFiles.some(f => f.id === selectedId)) {
            setSelectedId(eligibleFiles[0].id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligibleFiles])

    // EDF is EEG-only. Match Data Review exactly: a recording "has EEG" when its
    // montage mapping is non-empty. The measurement table can't tell a real-EEG
    // recording from one where the EEG basename is registered but has no channels,
    // only the archive (montage mapping) can. So fetch it per selected file and
    // cache by id. true = has EEG, false = none, "loading"/undefined = pending.
    const [eegByFileId, setEegByFileId] = useState({})
    useEffect(() => {
        if (!selectedId || eegByFileId[selectedId] !== undefined) return undefined
        let cancelled = false
        setEegByFileId(prev => ({ ...prev, [selectedId]: "loading" }))
        endpointProvider.post(LINKS.VISUALIZE.EEG.GET_MONTAGE_MAPPING, { data_object_id: selectedId })
            .then(mapping => {
                if (cancelled) return
                const hasEeg = !!mapping && Object.keys(mapping).length > 0
                setEegByFileId(prev => ({ ...prev, [selectedId]: hasEeg }))
            })
            .catch(() => {
                // Fail open: a lookup error shouldn't block EDF; the worker's
                // no-EEG guard is the backstop.
                if (!cancelled) setEegByFileId(prev => ({ ...prev, [selectedId]: true }))
            })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId])

    // EDF is allowed ONLY once we've confirmed the file has EEG. While the lookup
    // is pending (or on a no-EEG file) the EDF card is disabled, so it can't be
    // clicked during the request window. Distinguish the two states for the tooltip.
    const eegState = selectedFile ? eegByFileId[selectedFile.id] : undefined
    const edfAllowed = eegState === true
    const edfChecking = !!selectedFile && (eegState === undefined || eegState === "loading")
    const edfNoEeg = eegState === false
    const edfDisabled = !!selectedFile && !edfAllowed
    // If EDF is selected but not (yet) allowed for the current file, fall back to
    // HDF5 once we know it's a no-EEG file. While still checking we leave the
    // selection alone but the Start button is blocked (see handleConfirm).
    useEffect(() => {
        if (edfNoEeg && selectedFormat === "EDF") {
            setSelectedFormat("HDF5")
        }
    }, [edfNoEeg, selectedFormat])

    // Rough pre-flight check: estimate output ≈ source archive size and compare
    // against available space. Only blocks when we have BOTH a real free-space
    // number AND a known file size — otherwise fail open.
    const estimatedSize = Number(selectedFile?.size || 0)
    const insufficientStorage =
        diskFreeBytes !== null &&
        estimatedSize > 0 &&
        estimatedSize > diskFreeBytes

    const handleConfirm = async () => {
        if (!selectedId) return
        // Backstop for the EDF race: never submit EDF unless EEG is confirmed for
        // this file (covers a click landing before the montage lookup returns).
        if (selectedFormat === "EDF" && !edfAllowed) return
        setSubmitting(true)
        setError(null)
        try {
            const body = { data_object_id: selectedId, format: selectedFormat }
            if (selectedFormat === "CSV") {
                body.include_eeg = includeEEG
            }
            const result = await endpointProvider.post(LINKS.DATA.EXPORTS.START, body)
            close()
            onSuccess?.(result?.job_id)
        } catch (err) {
            setError(err?.message || "Export request failed.")
            setSubmitting(false)
        }
    }

    return (
        <Container>
            <HeaderRow>
                <Header>Export</Header>
                <IconButton
                    onClick={close}
                    aria-label="Close"
                    size="small"
                    sx={{ color: "var(--text-secondary-color)", marginTop: "-4px", marginRight: "-8px" }}
                >
                    <MdClose size={22} />
                </IconButton>
            </HeaderRow>
            <Subheader>
                Patient {patient?.patient_id || patient?.patient_key || ""}
                {patient?.first_name || patient?.last_name
                    ? ` — ${patient?.first_name || ""} ${patient?.last_name || ""}`
                    : ""}
            </Subheader>

            <SectionLabel>
                <span>Select a file</span>
                {eligibleFiles.length > 0 && (
                    <LabelCount>
                        {eligibleFiles.length} {eligibleFiles.length === 1 ? "file" : "files"} available
                    </LabelCount>
                )}
            </SectionLabel>
            {eligibleFiles.length === 0 ? (
                <div style={{ marginBottom: 20, color: "var(--text-secondary-color)" }}>
                    No eligible CNS files for this patient.
                </div>
            ) : (
                <div style={{ marginBottom: 20 }}>
                    <FileSelectDropdown
                        files={eligibleFiles}
                        value={selectedId}
                        onChange={setSelectedId}
                        placeholder="Select a file"
                    />
                </div>
            )}

            <SectionLabel><span>Select an output format</span></SectionLabel>
            <FormatRow>
                {EXPORT_FORMATS.map(fmt => {
                    // EDF is EEG-only; it's disabled until EEG is confirmed for the
                    // selected file (so it can't be clicked while the check is in flight).
                    const isEdf = fmt.id === "EDF"
                    const disabled = !fmt.available || (isEdf && edfDisabled)
                    const tip = isEdf && edfNoEeg
                        ? "This recording has no EEG. EDF export includes EEG channels only."
                        : isEdf && edfChecking
                            ? "Checking for EEG…"
                            : !fmt.available
                                ? `${fmt.label} (coming soon)`
                                : ""
                    // Not HTML-disabled (so the tooltip still fires on hover); the
                    // click is guarded and aria-disabled conveys the state.
                    return (
                        <Tooltip
                            key={fmt.id}
                            title={tip}
                            arrow
                            placement="top"
                            disableHoverListener={!tip}
                        >
                            <FormatCard
                                type="button"
                                $active={selectedFormat === fmt.id}
                                $disabled={disabled}
                                aria-disabled={disabled}
                                onClick={() => !disabled && setSelectedFormat(fmt.id)}
                            >
                                <FormatCardTitle $active={selectedFormat === fmt.id}>{fmt.label}</FormatCardTitle>
                                <FormatCardDesc>{fmt.desc}</FormatCardDesc>
                            </FormatCard>
                        </Tooltip>
                    )
                })}
            </FormatRow>
            <InfoSlot>
            {selectedFormat === "HDF5" && (
                <InfoCard>
                    <InfoIconWrapper>
                        <MdInfo size={20} style={{ color: '#207dea' }} />
                    </InfoIconWrapper>
                    <InfoText>
                        HDF5 preserves every signal at full resolution in a single
                        .h5 file. Best for analysis pipelines and re-import.
                    </InfoText>
                </InfoCard>
            )}
            {selectedFormat === "EDF" && (
                <InfoCard>
                    <InfoIconWrapper>
                        <MdInfo size={20} style={{ color: '#207dea' }} />
                    </InfoIconWrapper>
                    <InfoText>
                        EDF exports include EEG channels only. Cancel and live
                        progress are limited for EDF — the job will run to
                        completion and the progress bar stays at 0% until it
                        finishes.
                    </InfoText>
                </InfoCard>
            )}
            {selectedFormat === "CSV" && (
                <InfoCard>
                    <InfoIconWrapper>
                        <MdInfo size={20} style={{ color: '#207dea' }} />
                    </InfoIconWrapper>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <InfoText>
                            CSV produces a zip file with one CSV per modality.
                            Includes annotations.csv when present.
                        </InfoText>
                        <label style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginTop: 10,
                            cursor: "pointer",
                            fontSize: 13,
                            color: "var(--color-text-primary)",
                            fontFamily: "'Source Sans Pro'",
                        }}>
                            <input
                                type="checkbox"
                                checked={includeEEG}
                                onChange={(e) => setIncludeEEG(e.target.checked)}
                                style={{ marginTop: 2, cursor: "pointer" }}
                            />
                            <span>
                                Include EEG
                                <span style={{
                                    color: "var(--text-secondary-color)",
                                    fontStyle: "italic",
                                    marginLeft: 6,
                                }}>
                                    (slows conversion ~2x and roughly triples the download size)
                                </span>
                            </span>
                        </label>
                    </div>
                </InfoCard>
            )}
            </InfoSlot>

            {insufficientStorage && (
                <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    background: "rgba(244, 67, 54, 0.08)",
                    border: "1px solid rgba(244, 67, 54, 0.25)",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginBottom: 12,
                    fontSize: 13,
                    color: "var(--color-text-primary)",
                }}>
                    <MdInfo size={18} style={{ color: "#F44336", flexShrink: 0, marginTop: 1 }} />
                    <span>
                        <strong style={{ color: "#F44336" }}>Not enough storage.</strong>{" "}
                        This export needs about <strong>{humanReadableSize(estimatedSize)}</strong> but
                        only <strong>{humanReadableSize(diskFreeBytes)}</strong> is free. Delete old
                        exports from the Export Tracker to free up space, then try again.
                    </span>
                </div>
            )}

            {error && (
                <div style={{ color: "var(--color-danger, #d32f2f)", marginBottom: 12 }}>
                    {error}
                </div>
            )}

            <ButtonRow>
                <MobergButton
                    variant={MobergButtonVariant.OUTLINE}
                    theme={MobergTheme.RED}
                    shape={MobergButtonShape.RECTANGLE}
                    onClick={close}
                    disabled={submitting}
                >
                    Cancel
                </MobergButton>
                <MobergButton
                    variant={MobergButtonVariant.FILLED}
                    theme={MobergTheme.BLUE}
                    shape={MobergButtonShape.RECTANGLE}
                    onClick={handleConfirm}
                    disabled={
                        !selectedId || submitting || insufficientStorage
                        || (selectedFormat === "EDF" && !edfAllowed)
                    }
                >
                    {submitting ? "Starting…" : "Start Export"}
                </MobergButton>
            </ButtonRow>
        </Container>
    )
}
